import { useState, useEffect } from 'react';

import { useRepoParams } from '../../../hooks/useRepoParams';
import { pullRequestApi } from '../../../api/client';
import { DiffView } from '../../../components/DiffView';
import './PullRequestDetailPage.css';

export function PullRequestDetailPage() {
    const { owner, repo, id } = useRepoParams();
    const repoPath = `${owner}/${repo}`;

    const [pr, setPr] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview'); // overview, commits, changes
    const [merging, setMerging] = useState(false);

    useEffect(() => {
        loadPr();
    }, [id]);

    const loadPr = async () => {
        try {
            setLoading(true);
            const data = await pullRequestApi.get(repoPath, id!);
            setPr(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleMerge = async () => {
        if (!confirm('Are you sure you want to merge this pull request?')) return;

        try {
            setMerging(true);
            await pullRequestApi.merge(repoPath, id!);
            alert('Merged successfully!');
            loadPr(); // Reload to update status
        } catch (err: any) {
            alert('Merge failed: ' + err.message);
        } finally {
            setMerging(false);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (!pr) return <div className="error">Pull Request not found</div>;

    const { comparison } = pr;

    return (
        <div className="pr-detail-page">
            <div className="pr-header">
                <div className="pr-title-row">
                    <h1>{pr.title}</h1>
                    <span className={`status-badge status-${pr.status}`}>{pr.status}</span>
                </div>
                <div className="pr-meta">
                    <b>{pr.author?.username}</b> wants to merge into <span className="branch-name">{pr.targetBranch}</span> from <span className="branch-name">{pr.sourceBranch}</span>
                </div>
            </div>

            <div className="pr-tabs">
                <button
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`tab ${activeTab === 'commits' ? 'active' : ''}`}
                    onClick={() => setActiveTab('commits')}
                >
                    Commits ({comparison?.commits?.length || 0})
                </button>
                <button
                    className={`tab ${activeTab === 'changes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('changes')}
                >
                    Changes ({comparison?.files?.length || 0})
                </button>
            </div>

            <div className="pr-content">
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="description-box">
                            {pr.description ? (
                                <pre>{pr.description}</pre>
                            ) : (
                                <em>No description provided.</em>
                            )}
                        </div>

                        <div className="merge-box">
                            {pr.status === 'open' && (
                                <div className="merge-actions">
                                    {comparison?.canMerge ? (
                                        <div className="can-merge">
                                            <div className="merge-status-icon success">✓</div>
                                            <div className="merge-info">
                                                <strong>This branch has no conflicts with the base branch</strong>
                                                <p>Merging can be performed automatically.</p>
                                            </div>
                                            <button
                                                className="btn btn-success"
                                                onClick={handleMerge}
                                                disabled={merging}
                                            >
                                                {merging ? 'Merging...' : 'Merge Pull Request'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="cannot-merge">
                                            <div className="merge-status-icon error">!</div>
                                            <div className="merge-info">
                                                <strong>This branch has conflicts that must be resolved</strong>
                                            </div>
                                            <button className="btn" disabled>Merge Pull Request</button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {pr.status === 'merged' && (
                                <div className="merged-msg">
                                    Pull request successfully merged and closed
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'commits' && comparison && (
                    <div className="commits-tab">
                        {comparison.commits.map((commit: any) => (
                            <div key={commit.sha} className="commit-item">
                                <div className="commit-msg">{commit.message}</div>
                                <div className="commit-sha">{commit.sha.substring(0, 7)}</div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'changes' && comparison && (
                    <div className="changes-tab">
                        <DiffView files={comparison.files} stats={{ changed: comparison.files.length, additions: 0, deletions: 0 }} />
                    </div>
                )}
            </div>
        </div>
    );
}
